import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import type { PostingRequirementsDef } from '../posting-requirements/requirements-key';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useJobPostingChannels } from '../../use-job-posting-channels';
import { ZIPRECRUITER } from '@/types/channels';
import { getProgress } from '@/utils/progress';

import { isBlankAddressField } from '../hiring-address';
import { shouldShowField } from '../posting-requirements/display-rules';

interface UseOpeningProgressParams {
  opening: OpeningDetailResponse;
  contractRequirements: PostingRequirementsDef[];
}

/**
 * Computes completion progress for a single opening's job detail form.
 *
 * "In a VONQ channel" is scoped to this specific opening (i.e. the opening
 * must appear in the channel's openingBudgets), so progress counts VONQ-
 * required fields only when they actually apply to this opening.
 *
 * Display-rule-hidden fields are excluded from both totalRequiredCount and
 * filledRequiredCount so the progress bar reflects only visible fields.
 * vonqPostingRequirements is watched reactively so counts update immediately
 * when sibling field values change and show/hide dependent fields.
 */
export function useOpeningProgress({
  opening,
  contractRequirements,
}: UseOpeningProgressParams) {
  const { hookForm } = useJobPostingContext();
  const { selectedChannelSlugs } = useJobPostingChannels();
  const {
    formState: { errors },
  } = hookForm;
  const isStateCodeRequired =
    selectedChannelSlugs.includes(ZIPRECRUITER) && opening.remote === true;

  const channels = useWatch({
    control: hookForm.control,
    name: 'channels',
  });

  // Reactive watch so progress updates when sibling values change display_rules.
  const vonqPostingRequirements =
    useWatch({
      control: hookForm.control,
      name: `jobDetails.${opening.external_id}.vonqPostingRequirements`,
    }) ?? {};

  // Watched by value: a blank address field is a gap whether or not a Save
  // has validated it, so a Discard that clears errors leaves the count alone.
  const hiringAddress = useWatch({
    control: hookForm.control,
    name: [
      `jobDetails.${opening.external_id}.streetAddress`,
      `jobDetails.${opening.external_id}.city`,
      `jobDetails.${opening.external_id}.postalCode`,
    ],
  });

  const isInVonqChannel = useMemo(
    () =>
      Object.values(channels ?? {}).some(
        channel =>
          !!channel?.vonqDetails &&
          opening.external_id in (channel.openingBudgets ?? {}),
      ),
    [channels, opening.external_id],
  );

  const jobDetailErrors = errors?.jobDetails?.[opening.external_id];

  const totalRequiredCount = useMemo(() => {
    const commonRequired =
      2 + // title + description always required
      (isInVonqChannel ? 7 : 0) + // industry, jobHours, jobType, experience, locationAddress, postalCode, city
      (isStateCodeRequired ? 1 : 0); // stateCode when ZipRecruiter lists a remote opening
    const contractRequired = contractRequirements.reduce(
      (sum, contract) =>
        sum +
        contract.posting_requirements.filter(
          f =>
            f.required &&
            f.type !== 'STATISCH' &&
            f.type !== 'QUESTIONNAIRE' &&
            shouldShowField(
              f,
              vonqPostingRequirements[contract.key] ?? {},
              contract.posting_requirements,
            ),
        ).length,
      0,
    );
    return commonRequired + contractRequired;
  }, [
    contractRequirements,
    isInVonqChannel,
    isStateCodeRequired,
    vonqPostingRequirements,
  ]);

  const filledRequiredCount = useMemo(() => {
    const commonErrorCount = [
      jobDetailErrors?.title,
      jobDetailErrors?.description,
      ...(isInVonqChannel
        ? [
            jobDetailErrors?.industry,
            jobDetailErrors?.jobHours,
            jobDetailErrors?.jobType,
            jobDetailErrors?.experience,
            ...(opening.remote
              ? []
              : [
                  jobDetailErrors?.locationAddress,
                  jobDetailErrors?.postalCode,
                  jobDetailErrors?.city,
                ]),
          ]
        : []),
      ...(isStateCodeRequired ? [jobDetailErrors?.stateCode] : []),
    ].filter(Boolean).length;
    const blankAddressCount =
      isInVonqChannel && opening.remote
        ? hiringAddress.filter(isBlankAddressField).length
        : 0;

    const contractEmptyCount = contractRequirements.reduce((sum, contract) => {
      const contractValues = vonqPostingRequirements[contract.key] ?? {};
      const contractErrors =
        (
          jobDetailErrors?.vonqPostingRequirements as
            | Record<string, Record<string, unknown>>
            | undefined
        )?.[contract.key] ?? {};
      return (
        sum +
        contract.posting_requirements.filter(field => {
          if (!field.required) return false;
          if (field.type === 'STATISCH' || field.type === 'QUESTIONNAIRE')
            return false;
          if (
            !shouldShowField(
              field,
              contractValues,
              contract.posting_requirements,
            )
          )
            return false;
          const value = contractValues[field.name];
          const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
          const hasError = !!contractErrors[field.name];
          return isEmpty || hasError;
        }).length
      );
    }, 0);

    return (
      totalRequiredCount -
      commonErrorCount -
      blankAddressCount -
      contractEmptyCount
    );
  }, [
    jobDetailErrors,
    contractRequirements,
    vonqPostingRequirements,
    hiringAddress,
    isInVonqChannel,
    isStateCodeRequired,
    opening.remote,
    totalRequiredCount,
  ]);

  const fieldsLeft = Math.max(0, totalRequiredCount - filledRequiredCount);
  const isComplete = fieldsLeft === 0;
  const progress = getProgress(
    totalRequiredCount,
    Math.max(0, filledRequiredCount),
  );

  return {
    isInVonqChannel,
    totalRequiredCount,
    filledRequiredCount,
    fieldsLeft,
    isComplete,
    progress,
  };
}
